import React, { useState, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Empty, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
import useBankActions from "../../../customHooks/GET_requests/useBankActions";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import MonitoringHeartBeat from "../monitoring/MonitoringHeartBeat";
import ControlPanelLiftScheduledSection from "./ControlPanelLiftScheduledSection";
import ActionsPanel from "../Actions/ActionsPanel";
import ActionCard from "../Actions/components/ActionCard";
// INTERFACES
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { BankActionInterface, BankFloorInterface } from "../../../interfaces/Bank.interface";
import { actionAppliesToLift, actionIsBankLevel } from "../Actions/actionLiftScope";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// CONFIG
import { actionTypeOrDefault } from "../Actions/actionTypeConfig";
// UTILS
import { eligibleFloorOpenings, actionTakesFloor } from "../../../utils/controlFloorRules";
import { extractBankFloorOpenings } from "../../../utils/bankFloorOpenings";
// UUID
import { stringToUuidT } from "../../../utils/uuid";
import formatUuidT from "../../../flatbuffers/display/uuid";
// PERMISSIONS
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// SCOPE
import { useScopePredicate } from "../../../customHooks/useScopedPermission";
import { PermissionAction } from "../../../flatbuffers/generated/archive/permission-action";

export default function ControlPanelLiftActions({
  isControlPanel,
  bankId,
  bankName,
}: {
  isControlPanel: boolean;
  bankId: string;
  bankName: string;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [disableTurnBack, setDisabledTurnBack] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme, userLogged } = useSelector((state: RootState) => state.home);
  const { liftSelected, bankSelected } = useSelector((state: RootState) => state.monitoring);
  const { permissionSet } = useSelector((state: RootState) => state.user);
  // Scheduled actions live inside this lift "Actions" tab by design (unlike the
  // bank control panel, where they get their own top-level SCHEDULED_ACTIONS tab
  // gated solely on ScheduledActionsView). The parent tab is gated on
  // MonitorControlPanelActions (ControlPanelLift.tsx), so on the lift surface the
  // scheduled section is intentionally reachable only with BOTH that tab
  // permission AND ScheduledActionsView — it is a sub-capability of the Actions
  // tab here. The section component self-gates on ScheduledActionsView and
  // renders null without it; we gate the heading on the same permission so we
  // never show an orphaned "SCHEDULED" title above an empty section.
  const canViewScheduledActions = hasPermission(
    Permission.ScheduledActionsView,
    permissionSet ?? [],
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  /**
   * This bank's configured actions. The fetch, the row mapping and the message-id
   * normalisation all moved into `useBankActions`, so the bank Hall Calls / VIP
   * panels and the lift Car Calls panel read the SAME rows this list renders
   * rather than the `bankSelected.actions` relation that is absent on the
   * FlatBuffer path.
   */
  const { actions } = useBankActions(bankId);
  const liftUuid = useMemo(() => stringToUuidT(String(liftSelected.id ?? "")), [liftSelected.id]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const { messages } = useGetAllMessages();

  // ************************************************ */
  // FLOOR ELIGIBILITY ****************************** */
  // Per-floor ACTION scope predicate — empty grant rows ⇒ allow-all.
  const floorActionAllows = useScopePredicate(PermissionAction.ACTION);

  /**
   * Bank floors actually served by the currently selected lift.
   *
   * Without this the strip renders EVERY bank floor, so on a bank whose lifts
   * serve distinct floor sets the operator is offered another lift's floors. An
   * empty or absent set means no per-lift restriction (serve all bank floors),
   * which preserves behaviour for banks with no lift_floors rows.
   */
  const liftServedFloorIds = useMemo(() => {
    const set = new Set<string>();
    (liftSelected.liftFloors ?? []).forEach((lf) => {
      const id = formatUuidT(lf.bankFloorId);
      if (id) set.add(id);
    });
    return set;
  }, [liftSelected.liftFloors]);

  /**
   * An action's bound Control enum, or undefined when it resolves to none.
   *
   * "Unresolved" MUST be undefined, never 0. Control is an unnumbered,
   * append-only enum, so declaration order IS the wire value and the first
   * member — Control.CarCallLock — is a legitimate 0. Using 0 as the
   * absent-sentinel silently drops every CarCallLock action while 1-19 behave.
   */
  const controlEnumOf = useMemo(() => {
    const byId = new Map<string, number>();
    messages.forEach((m) => {
      const raw =
        (m as { enumId?: number; enum_id?: number }).enumId ?? (m as { enum_id?: number }).enum_id;
      if (typeof raw === "number") byId.set(String(m.id), raw);
    });
    return (action: BankActionInterface): number | undefined => byId.get(String(action.messageId));
  }, [messages]);

  /**
   * The bank's floors as opening records — the shape the shared eligibility rule
   * reads. `extractBankFloorOpenings` is what recovers a usable vertical order
   * when the `position` column is degenerate (identical, typically 0, across
   * every floor), which is the state real banks are in. Ranking on
   * `position ?? floorIndex` cannot: `??` does not fall back on a present 0, so
   * the top and the bottom read as the same floor and every up/down hall call
   * was excluded.
   */
  const bankFloorOpenings = useMemo(
    () => extractBankFloorOpenings({ bankFloors: bankSelected.bankFloors ?? [] }),
    [bankSelected.bankFloors],
  );

  /**
   * The floors one row may be sent to, or [] when the row takes no floor.
   *
   * An empty array is what tells ActionCard to render no strip, so the two
   * questions — "does this command take a floor" and "which floors can it
   * reach" — resolve in one place and cannot disagree. `actionTakesFloor` owns
   * the first, including the guard that an unresolvable command gets no strip.
   *
   * Matching is by floor id, not position, because on exactly the banks whose
   * position column is degenerate a position cannot identify a floor.
   */
  const floorsForAction = useMemo(() => {
    const bankFloors = (bankSelected.bankFloors ?? []) as BankFloorInterface[];

    return (action: BankActionInterface): BankFloorInterface[] => {
      const enumValue = controlEnumOf(action);
      if (
        !actionTakesFloor(action.takesPosition, enumValue, action.floorScope) ||
        enumValue === undefined
      )
        return [];
      if (bankFloors.length === 0) return [];

      const eligibleIds = new Set(
        eligibleFloorOpenings(enumValue, bankFloorOpenings).map((opening) => opening.id),
      );

      return (
        bankFloors
          .filter((floor) => eligibleIds.has(formatUuidT(floor.id)))
          .filter(
            (floor) =>
              liftServedFloorIds.size === 0 || liftServedFloorIds.has(formatUuidT(floor.id)),
          )
          // Out-of-scope floors are HIDDEN rather than disabled. Master admin and
          // empty grant rows ⇒ allow-all.
          .filter((floor) =>
            floorActionAllows({
              bankId,
              liftId: formatUuidT(liftSelected.id),
              bankFloorId: formatUuidT(floor.id),
            }),
          )
          // Top floor first, so the strip reads the way the building stands.
          .sort((a: BankFloorInterface, b: BankFloorInterface) => b.floorIndex - a.floorIndex)
      );
    };
  }, [
    bankSelected.bankFloors,
    bankFloorOpenings,
    controlEnumOf,
    liftServedFloorIds,
    floorActionAllows,
    bankId,
    liftSelected.id,
  ]);

  // ************************************************ */
  // ACTION LIST *********************************** */
  /**
   * ONE card per row `/action-banks/by-bank/:bankId` returned, in configured
   * order (createdAt) — the same rows, in the same order, that the setup
   * wizard's Actions table shows for this bank, restricted to THIS car.
   *
   * Bank-level rows (no lift scope) are excluded here even though
   * `actionAppliesToLift` would otherwise pass them everywhere — those now
   * live exclusively on the bank drawer's own Actions tab
   * (`ControlPanelBankActions`), so a hall-wide/bank-wide command shows in
   * exactly one place instead of duplicated across every lift's tab.
   */
  const cards = useMemo(
    () =>
      actions
        .filter((action: BankActionInterface) => action.deletedAt == null)
        // Unit-level commands only: bank-level rows belong to the bank
        // drawer's Actions tab, not any single car's.
        .filter((action: BankActionInterface) => !actionIsBankLevel(action))
        .filter((action: BankActionInterface) =>
          actionAppliesToLift(action, formatUuidT(liftSelected.id)),
        )
        .slice()
        .sort((a: BankActionInterface, b: BankActionInterface) =>
          a.createdAt.localeCompare(b.createdAt),
        )
        .map((action: BankActionInterface) => ({
          action,
          type: actionTypeOrDefault(action.buttonType),
          // Resolved once, here, rather than inside each card: the row stays in
          // the list either way — this list owes the setup wizard's Actions
          // table a row-for-row match — and the floors only decide whether that
          // row also gets a selector.
          floors: floorsForAction(action),
        })),
    [actions, floorsForAction],
  );

  /**
   * The car's live service mode, as a Service enum code.
   *
   * This is handed to every action button as `carService`, and each one
   * compares it against the enum values its configured validStates resolve
   * to. It must therefore be the code, not the label: this was previously
   * `Number(getServiceModeDisplay(...).displayName)`, and `Number("Automatic")`
   * is NaN, so no action ever matched its own valid state.
   *
   * The absent sentinel is -1, matching applyDeltaToState's variantMessage.
   * It cannot be 0 — that is Service.Auto, so a lift with no service message
   * yet would read as already being in Auto.
   */
  const carServiceCode = useMemo(() => {
    const code = socketMessageLift.serviceMode?.message?.message?.message;
    return typeof code === "number" ? code : -1;
  }, [socketMessageLift.serviceMode]);

  // ************************************************ */
  // COMPONENT ************************************** */
  const hasCards = cards.length > 0;

  return (
    <div className="monitoringView__detailsBox" style={{ minHeight: "400px", height: "auto" }}>
      <div
        className={`monitoringView__controlPanel monitoringView__controlPanel__${theme}`}
        style={{
          minHeight: "400px",
          height: "auto",
          paddingBottom: "25px",
        }}
      >
        {!isControlPanel && (
          <div className="monitoring__card__header mb-2">
            <div className="generalStyles__flex">
              <MonitoringHeartBeat bankId={bankId} type="TOOLTIP" />
              <div className="generalStyles__textEllipsis" role="button">
                <Tooltip placement="topLeft" title={bankName || bankSelected.name || ""}>
                  <h6>{bankName || bankSelected.name || ""}</h6>
                </Tooltip>
              </div>
            </div>
          </div>
        )}

        {/** ******************************************************** */}
        {/** ACTIONS — one card per row the shared by-bank endpoint returned, */}
        {/** in configured order (createdAt). */}
        <ActionsPanel
          title={t("general.actions", "Actions")}
          subtitle={t(
            "monitoringActions.panelSubtitle",
            "Execute configured commands for this elevator.",
          )}
          helpText={t(
            "monitoringActions.panelHelp",
            "Each row is one configured command. Buttons stay disabled while the car is already in the target state or while a command is in flight.",
          )}
          theme={theme}
        >
          {hasCards ? (
            cards.map((card) => (
              <ActionCard
                key={card.action.id}
                action={card.action}
                typeName={card.type.typeName}
                config={card.type.config}
                carService={carServiceCode}
                theme={theme}
                disableTurnBack={disableTurnBack}
                setDisableTurnBack={setDisabledTurnBack}
                floors={card.floors}
              />
            ))
          ) : (
            <div className="actionsPanel__section generalStyles__flexCenter">
              <Empty description={<span>{t("general.noActionsAssigned")}</span>} />
            </div>
          )}
        </ActionsPanel>

        {/** ******************************************************** */}
        {/** SCHEDULED ACTIONS — independent of manual actions, gated on the */}
        {/** dedicated scheduled-actions view permission. */}
        {canViewScheduledActions && (
          <div className={`actionsPanel actionsPanel__${theme} mt-3`}>
            <h5 className="actionsPanel__sectionTitle">
              {t("scheduledActions.scheduledActionsTab", "SCHEDULED")}
            </h5>
            <div className="actionsPanel__section">
              <ControlPanelLiftScheduledSection
                liftId={formatUuidT(liftSelected.id)}
                bankId={bankId}
                organizationId={userLogged?.organizationId ?? ""}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
